import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';

// core components
import { green } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Button,
  IconButton,
  Snackbar,
  SnackbarContent,
} from '@mui/material';

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import KSpinner from '../components/KSpinner';
import CrmTextField from '../components/CrmTextField';

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
  snackbarSuccess: {
    backgroundColor: green[600],
  },
  snackbarMessage: {
    display: 'flex',
    alignItems: 'center',
  },
  snackbarIcon: {
    marginRight: theme.spacing(1),
  },
});

const syncApiEndpoint = '/api/superAdmin/emrMappings';

const snackbarDefaults = {
  message: '',
  isOpen: false,
};

class ConfigureEmrSyncPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      unauthorized: false,
      firmIds: [],
      firmsWithMappings: {},
      // TODO: snackbar as its own component
      snackbar: snackbarDefaults,
    };
  }

  handleSnackbarClose() {
    this.setState({ snackbar: snackbarDefaults });
  }

  snackbarSuccess(message) {
    this.setState({
      snackbar: {
        message,
        isOpen: true,
      },
    });
  }

  componentDidMount() {
    const role = this.props.userInfo.role;
    const onLocalMachine = window.location.hostname === 'localhost';
    if (onLocalMachine || role === 'SuperAdmin') {
      this.fetchTableData();
    } else {
      this.setState({ unauthorized: true });
    }
  }

  fetchTableData() {
    this.setState({ querying: true });
    const request = createStdRequest(syncApiEndpoint);
    const actionOnGoodResponse = (json) => {
      const firmsWithMappings = {};
      json.forEach((firm) => (firmsWithMappings[firm.firmId] = firm));

      this.setState({ querying: false, firmsWithMappings, firmIds: json.map((firm) => firm.firmId) });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  changeMappedEmr(firmId, mappedEmrName) {
    const updatedFirm = { ...this.state.firmsWithMappings[firmId], emrTenant: mappedEmrName };
    this.setState({ firmsWithMappings: { ...this.state.firmsWithMappings, [firmId]: updatedFirm } });
  }

  handleSubmit() {
    this.postEmrSyncConfig(this.state.firmsWithMappings);
  }

  postEmrSyncConfig(data) {
    const apiStruct = this.buildApiStruct(data);
    const request = createStdRequest(syncApiEndpoint, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.snackbarSuccess('EMR Sync Settings updated successfully.');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildApiStruct(data) {
    // create an array with a record for each firm to send back to the api
    return this.state.firmIds.map((firmId) => data[firmId]);
  }

  render() {
    const { classes } = this.props;

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.querying) {
      return (
        <div style={{ textAlign: 'center' }}>
          <KSpinner />
        </div>
      );
    }

    // TODO: we have this same code block (if statement) in 5 places currently, refactor to a component?
    if (this.state.unauthorized) {
      return (
        <div>
          <h2>Sorry! You're not allowed to be here.</h2>;<h3>You need to be logged in as a SuperAdmin.</h3>;
        </div>
      );
    }

    return (
      <div>
        <Card>
          <CardContent>
            For each Firm, you may specify the EMR rehab (domain name) to sync with. To disable sync, clear out the
            "Mapped EMR" field for a firm.
          </CardContent>
        </Card>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#e7e7e7' }}>
              <TableCell>Firm</TableCell>
              <TableCell>Mapped EMR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.firmIds.map((firmId) => (
              <TableRow key={firmId}>
                <TableCell>{this.state.firmsWithMappings[firmId].firmName}</TableCell>
                <TableCell>
                  <CrmTextField
                    value={this.state.firmsWithMappings[firmId].emrTenant || ''}
                    onChange={(event) => this.changeMappedEmr(firmId, event.target.value.trim())}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div>
          <Button color="primary" onClick={() => this.handleSubmit()}>
            Update Sync Configuration
          </Button>
        </div>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={this.state.snackbar.isOpen}
          autoHideDuration={4000}
          onClose={() => this.handleSnackbarClose()}
          aria-describedby="client-snackbar"
        >
          <SnackbarContent
            className={classes.snackbarSuccess}
            message={
              <span className={classes.snackbarMessage}>
                <CheckCircleIcon className={classes.snackbarIcon} />
                {this.state.snackbar.message}
              </span>
            }
            action={[
              <IconButton
                key="close"
                aria-label="close"
                color="inherit"
                onClick={() => this.handleSnackbarClose()}
                size="large"
              >
                <CloseIcon />
              </IconButton>,
            ]}
          />
        </Snackbar>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
});
const actionProducers = { setApiResponseInfo };
export default connect(mapStateToProps, actionProducers)(withStyles(styles)(ConfigureEmrSyncPage));
